import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import TopicPickerList from './TopicPickerList';
import { Portal } from 'react-portal';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import useReportIconAutoselect from 'components/Reports/useReportIconAutoselect';

import data from '@emoji-mart/data';
import { init } from 'emoji-mart';
import { useParams } from 'react-router';
import { useSiteSectionsNames } from 'utils/getAppSite';
import useRecentSearchesTopicPicker from 'components/Common/TopicPicker/useRecentSearchesTopicPicker';

const TopicPicker = (props) => {
  const [showDropdownContent, setShowDropdownContent] = useState(false);
  const { topicOrClientWord, newAgencyPlan } = useTopicOrClientWord();

  const {
    variant = 'default',
    allowFreeSearch,
    emptyInputStatus,
    keywordsLists,
    mainSearchValue,
    searchFunction = () => {},
    showTopicCases,
    closeButtonFunction,
    defaultTopic,
    fixedPosition,
    placeholder,
    disabled,
    closeButtonMessage = 'See all activity',
    topicButtonDataTip = `${!showDropdownContent ? `Select a ${topicOrClientWord}` : ''}`,
    closeButtonPosition = 'top',
    dataTooltip = 'general-tooltip',
    onlySearchbar,
    aiIcons,
    recentItemsCondition,
    selectedTopic,
    onSelectTopic,
    alwaysShowSelectedMark,
    showMasterTopicIcon,
    onDropdownStateChange,
  } = props;
  const [searchValue, setSearchValue] = useState(mainSearchValue ?? '');
  const [topic, setTopic] = useState(defaultTopic ?? keywordsLists[0]);
  const [customIcon, setCustomIcon] = useState('');

  const topicContainer = useRef(null);
  const dropdownElement = useRef(null);
  const inputElement = useRef(null);
  const searchTimeout = useRef(null);

  const activeTopic = selectedTopic ?? topic;
  const showTopic = allowFreeSearch ? !showDropdownContent && showTopicCases && searchValue === '' : showTopicCases;
  const { callLoggedOutIconSearch } = useReportIconAutoselect();
  const params = useParams();
  const { getTopicTitle } = useSiteSectionsNames();
  const { addRecentSearchToLocalStorage } = useRecentSearchesTopicPicker();
  const { event } = params;

  const iconCondition = showTopicCases || allowFreeSearch;

  const hideSearchBar = () => {
    setShowDropdownContent(false);
  };
  useOutsideElement(dropdownElement, hideSearchBar);

  const applySearchQueryOrTopic = ({ freeSearch, topic }) => {
    if (disabled) {
      return;
    }
    if (recentItemsCondition) {
      addRecentSearchToLocalStorage({ freeSearch, topic });
    }
    if (topic) {
      setTopic(topic);
      if (onSelectTopic) {
        onSelectTopic(topic);
      }
      setSearchValue('');
    }
    searchFunction({ freeSearch, topic });
    setShowDropdownContent(false);
  };

  const openSearchBar = () => {
    if (!onlySearchbar) {
      setShowDropdownContent(true);
    }
    setTimeout(() => {
      if (inputElement.current && allowFreeSearch) {
        inputElement.current.focus();
      }
    }, 100);
  };

  useEffect(() => {
    init({ data });
  }, []);

  useEffect(() => {
    setTopic(defaultTopic ?? keywordsLists[0]);
  }, [defaultTopic, keywordsLists]);

  useEffect(() => {
    if (selectedTopic !== undefined) {
      setTopic(selectedTopic);
    }
  }, [selectedTopic]);

  useEffect(() => {
    setSearchValue(mainSearchValue);
  }, [mainSearchValue]);

  useEffect(() => {
    if (onDropdownStateChange) {
      onDropdownStateChange(showDropdownContent);
    }
  }, [showDropdownContent, onDropdownStateChange]);
  const isMobile = useIsMobile();

  const onBlurFunction = async (e) => {
    const inputValue = searchValue;
    if (inputValue !== '' && aiIcons) {
      let value = await callLoggedOutIconSearch({ value: inputValue });
      if (value) {
        setCustomIcon(value);
      }
    }
  };

  return (
    <>
      {showDropdownContent && (
        <div className='general-close-background z-extra-max' onClick={() => hideSearchBar()}></div>
      )}
      <div
        className={`z-extra-max dropdown-keywords-lists ${variant === 'stakeholder' ? 'stakeholder-dropdown-list' : ''} ${
          showDropdownContent ? 'dropdown-keywords-lists-open' : ''
        } ${emptyInputStatus ? 'dropdown-keywords-influence' : ''} ${
          fixedPosition ? 'dropdown-keywords-lists-fixed-position' : ''
        } ${keywordsLists.length === 0 && showTopic ? 'dropdown-keyword-lists-empty' : ''} ${
          disabled ? 'dropdown-keywords-lists-disabled' : ''
        } ${newAgencyPlan ? 'dropdown-keywords-new-agency' : ''}`}
        ref={topicContainer}
      >
        <div
          className={`dropdown-keywords-activeitem d-flex align-items-center ${
            showTopic && !disabled ? 'pointer' : ''
          } `}
          onClick={(e) => {
            if (!disabled) {
              if (showDropdownContent && !allowFreeSearch) {
                hideSearchBar();
              } else {
                openSearchBar();
              }
            }
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* TYPE ICON */}
          {iconCondition && (
            <>
              {aiIcons && searchValue !== '' && customIcon ? (
                <span className='report-list-icon text-center d-inline-block-centered'>
                  <em-emoji set={'apple'} id={customIcon} size={'1.45em'} style={{ lineHeight: 1 }} />
                </span>
              ) : (
                <span
                  className={`main-icon-topic-picker ${
                    showTopic
                      ? `${showMasterTopicIcon && activeTopic?.id === null ? 'icon-topics-master' : 'icon-topics-thin'} ${
                          keywordsLists.length === 0 ? 'main-light-text' : ''
                        }`
                      : 'icon-search'
                  }`}
                />
              )}
            </>
          )}
          {/*INPUT SEARCHBAR */}
          {(!showTopic || keywordsLists.length === 0) && (
            <input
              className='search-main-input'
              ref={inputElement}
              value={searchValue ?? ''}
              onChange={(e) => {
                setSearchValue(e.target.value);
              }}
              placeholder={
                placeholder ??
                (allowFreeSearch ? `Search by keyword or ${topicOrClientWord}` : `Select a ${topicOrClientWord}`)
              }
              disabled={!allowFreeSearch || disabled}
              onKeyDown={(e) => {
                if (disabled) {
                  return;
                }
                if (e.key === 'Enter') {
                  applySearchQueryOrTopic({ freeSearch: e.target.value });
                }
              }}
              onKeyUp={(e) => {
                if (searchTimeout.current) clearTimeout(searchTimeout.current);
                searchTimeout.current = setTimeout(() => onBlurFunction(e), 500);
              }}
              style={!searchValue && !iconCondition ? { paddingLeft: '11px', paddingTop: '1px' } : undefined}
            />
          )}
          {showTopic && keywordsLists?.length > 0 && (
            <p className={`search-main-text-value ${!disabled ? 'pointer' : ''}`}>
              <span className='d-block hidden-lines hidden-one-line'>
                {!!event && activeTopic?.name?.startsWith('All updates')
                  ? activeTopic.name.replace('updates', 'remarks')
                  : getTopicTitle(activeTopic?.name ?? '')}
              </span>
            </p>
          )}

          {/* CLOSE BUTTON  */}

          <div
            className='close-icon-container'
            style={{
              paddingRight: isMobile ? 0 : '63px',
              visibility:
                closeButtonFunction && (allowFreeSearch ? showTopic || mainSearchValue !== '' : showTopic)
                  ? 'visible'
                  : 'hidden',
            }}
          >
            <button
              className='general-button close-icon'
              data-tooltip-content={isMobile ? '' : `${closeButtonMessage}`}
              data-tooltip-id={dataTooltip}
              data-tooltip-place={closeButtonPosition}
              onClick={(e) => {
                if (disabled) {
                  e.preventDefault();
                  return;
                }
                e.stopPropagation();
                if (closeButtonFunction) {
                  closeButtonFunction(inputElement);
                }
                setSearchValue('');
              }}
            />
          </div>

          {/* ARROW BUTTON */}
          {!onlySearchbar && (
            <span
              className={`search-dropdown ${showDropdownContent ? 'search-dropdown-open' : ''}`}
              data-tooltip-content={isMobile ? '' : topicButtonDataTip}
              data-tooltip-id={dataTooltip}
              data-tooltip-place='right'
              onClick={(e) => {
                if (showDropdownContent) {
                  e.stopPropagation();
                  setShowDropdownContent(!showDropdownContent);
                }
              }}
            ></span>
          )}
        </div>
        {fixedPosition ? (
          <Portal>
            <KeywordListComponent
              topicContainer={topicContainer}
              dropdownElement={dropdownElement}
              showDropdownContent={showDropdownContent}
              inputElement={inputElement}
              applySearchQueryOrTopic={applySearchQueryOrTopic}
              topic={activeTopic}
              recentItemsCondition={recentItemsCondition}
              setSearchValue={setSearchValue}
              selectedTopic={selectedTopic}
              alwaysShowSelectedMark={alwaysShowSelectedMark}
              showMasterTopicIcon={showMasterTopicIcon}
              {...props}
            />
          </Portal>
        ) : (
          <>
            <KeywordListComponent
              topicContainer={topicContainer}
              dropdownElement={dropdownElement}
              showDropdownContent={showDropdownContent}
              inputElement={inputElement}
              applySearchQueryOrTopic={applySearchQueryOrTopic}
              topic={activeTopic}
              recentItemsCondition={recentItemsCondition}
              setSearchValue={setSearchValue}
              selectedTopic={selectedTopic}
              alwaysShowSelectedMark={alwaysShowSelectedMark}
              showMasterTopicIcon={showMasterTopicIcon}
              {...props}
            />
          </>
        )}
      </div>
    </>
  );
};

const KeywordListComponent = (props) => {
  const {
    customClassOpen,
    showDropdownContent,
    fixedPosition,
    dropdownElement,
    allowFreeSearch,
    inputElement,
    keywordsLists,
    applySearchQueryOrTopic,
    topic,
    mainSearchValue,
    showTopicSelected,
    dataTooltip,
    showActions,
    topicContainer,
    setLocalURLState,
    recentItemsCondition,
    setSearchValue,
    limitToCountOfItems,
    selectedTopic,
    alwaysShowSelectedMark,
    showMasterTopicIcon,
  } = props;

  const [positionStyles, setPositionStyles] = useState({});

  useLayoutEffect(() => {
    const updatePositionStyles = () => {
      if (fixedPosition && topicContainer?.current) {
        const rect = topicContainer.current.getBoundingClientRect();
        setPositionStyles({
          position: 'fixed',
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    updatePositionStyles();
    window.addEventListener('resize', updatePositionStyles);

    return () => {
      window.removeEventListener('resize', updatePositionStyles);
    };
  }, [fixedPosition, topicContainer, showDropdownContent]);

  return (
    <div
      className={`${customClassOpen} open-dropdown d-${showDropdownContent ? 'block' : 'none'} ${
        fixedPosition ? 'dropdown-fixed-position' : ''
      }`}
      style={positionStyles}
      ref={dropdownElement}
    >
      <div className='open-dropdown-container'>
        <TopicPickerList
          inputSearch={allowFreeSearch ? inputElement : null}
          keywordsLists={keywordsLists}
          applySearchQueryOrTopic={applySearchQueryOrTopic}
          showDropdownContent={showDropdownContent}
          topic={topic}
          selectedTopic={selectedTopic}
          searchValue={mainSearchValue ?? ''}
          showTopicSelected={showTopicSelected}
          dataTooltip={dataTooltip}
          showActions={showActions}
          setLocalURLState={setLocalURLState}
          recentItemsCondition={recentItemsCondition}
          setSearchValue={setSearchValue}
          limitToCountOfItems={limitToCountOfItems}
          alwaysShowSelectedMark={alwaysShowSelectedMark}
          showMasterTopicIcon={showMasterTopicIcon}
        />
      </div>
    </div>
  );
};

export default TopicPicker;
